import React, { Component } from "react";
import axios from "axios";
import LiveReadKaraoke from "./liveReadPage/LiveReadKaraoke";

import ActIconGrid from "../components/ActIconGrid";
import FlatIconGrid from "../components/FlatIconGrid";
import { AppContext } from "../../context/AppContext";
import { withRouter } from "../navigation/withNavigation";
import { withNotifications } from "../../state/notifications/NotificationContext";
import { loadContext as loadContextService } from "../../services/projects";
import { urlMap, getAuthHeaders, stripHtml } from "../../utils";

class LiveReadPage extends Component {
  static contextType = AppContext;
  state = {

    videos: [],
    loading: true,
    error: null,
    
    recordModalOpen: false,
    useAvatar: true,
    avatarUrl: '',
    
    actCount: 3,
    projectTypeLoading: false,
  };

  // --- UI handlers for record modal & webcam ---
  openRecordModal = (payload) => {
    // when starting a live read, prefer real webcam (disable avatar)
    this.setState({ recordModalOpen: true, useAvatar: false, selectedTile: payload || null });
  };

  closeRecordModal = () => {
    this.setState({ recordModalOpen: false });
  };  

  componentDidMount() {
    // Ensure project and project type are available after refresh
    this.ensureProjectAndType();
    this.fetchVideos();    
  }

  ensureProjectAndType = async () => {
    try {
      const routeProjectId = this.props.params?.projectId || null;
      const ctxProject = this.context?.state?.currentProject || null;
      const effectiveProjectId = routeProjectId || ctxProject?.id || null;

      if (!effectiveProjectId) {
        // Nothing to load
        this.setState({ projectTypeLoading: false });
        return;
      }

      // If context lacks project or type, use the centralized service to seed
      const hasProject = !!(ctxProject && ctxProject.id === effectiveProjectId);
      const typeId = ctxProject?.project_type_key;
      const hasType = !!(typeId && (this.context?.state?.projectTypes?.[typeId] || this.context?.state?.currentProjectType));
      if (!hasProject || !hasType) {
        this.setState({ projectTypeLoading: true });
        try {
          await loadContextService(effectiveProjectId, this.context);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("Failed to load context:", e);
        } finally {
          this.setState({ projectTypeLoading: false });
        }
      } else {
        this.setState({ projectTypeLoading: false });
      }
    } catch {
      this.setState({ projectTypeLoading: false });
    }
  }

  
  fetchVideos = async () => {
    try {
      const selectedProject = this.context?.state?.currentProject || null;
      const projectId = selectedProject?.id || undefined;
      const { data } = await axios.get(urlMap.listVideos, {
        headers: getAuthHeaders(),
        params: projectId ? { projectId } : undefined,
      });

      const list = Array.isArray(data) ? data : [];
      const videoChapterIds = new Set(
        list
          .map((v) => v?.chapterId)
          .filter((x) => typeof x === 'string' && x.length > 0)
      );
      const thumbByChapter = new Map();
      list.forEach((v) => {
        const c = v?.chapterId;
        if (c && v?.thumbnailUrl) thumbByChapter.set(c, v.thumbnailUrl);
      });

      this.setState({
        videos: list,
        videoChapterIds,
        videoThumbByChapter: thumbByChapter,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error("Error fetching videos:", err);
      this.setState({ error: "Failed to load videos", loading: false });
    }
  };

  // Rough reading time: assume ~200 wpm; minimum 1 min
  computeReadingTime = (text) => {
    try {
      if (!text || typeof text !== 'string') return null;
      const plain = stripHtml(text);
      const words = plain.trim().split(/\s+/).filter(Boolean).length;
      if (!words) return null;
      const minutes = Math.max(1, Math.round(words / 200));
      return `${minutes} min`;
    } catch {
      return null;
    }
  };

  getSelectedChapter = () => {
    try {
      const selId = this.state?.selectedTile?.id;
      if (!selId) return null;
      const chapters = this.context?.state?.currentChapters || [];
      return Array.isArray(chapters) ? chapters.find((c) => c?.id === selId) || null : null;
    } catch {
      return null;
    }
  };

  // Build a few overlay lines from chapter content (first 2-3 sentences/paragraphs)
  getOverlayLines = (content) => {
    if (!content || typeof content !== 'string') return [];
    // Split by sentence enders or line breaks, keep non-empty bits
    const chunks = content
      .split(/(?<=[.!?])\s+|\n+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return chunks.slice(0, 3);
  };

  render() {
    const { videos, loading, error, recordModalOpen, actCount } = this.state;
    const videoChapterIds = this.state.videoChapterIds || new Set();

    // Pull the selected project from AppContext
    const selectedProject = this.context?.state?.currentProject || null;
    const projectType = this.context?.state?.currentProjectType || null;
    const contextChapters = this.context?.state?.currentChapters || [];
    const activeChapters = Array.isArray(contextChapters)
      ? contextChapters.filter((c) => !c?.archived_at)
      : [];

    // Build acts from chapters, using project type to order/name acts when available
    const actsFromChapters = (() => {
      if (!selectedProject?.project_type_key) return null;
      const byKey = new Map();
      activeChapters.forEach((c) => {
        const key = c?.act_key || "__no_act__";
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(c);
      });

      // Candidate acts from project type for ordering and titles
      const pt = projectType;
      const candidates = pt && (Array.isArray(pt?.acts) ? pt.acts : Array.isArray(pt?.structure?.acts) ? pt.structure.acts : null);
      const ordered = [];
      if (Array.isArray(candidates) && candidates.length) {
        candidates.forEach((a, idx) => {
          const key = a?.key || a?.id || a?.uuid || `act_${(a?.number || a?.index || idx + 1) || idx + 1}`;
          const actNum = Number(a?.number || a?.index || idx + 1) || idx + 1;
          const title = a?.title || a?.name || null;
          const list = byKey.get(key) || [];
          if (byKey.has(key)) byKey.delete(key);
          const tiles = list.map((ch, i) => ({
            id: ch.id,
            label: ch.title || `Chapter ${ch.chapter_number}`,
            readingTime: this.computeReadingTime(ch?.content),
          }));
          ordered.push({ act: actNum, title, tiles });
        });
      }

      // Append any remaining grouped chapters (unknown keys) as extra acts
      let nextAct = ordered.length ? Math.max(...ordered.map((x) => x.act)) + 1 : 1;
      for (const [k, list] of byKey.entries()) {
        const tiles = list.map((ch, i) => ({
          id: ch.id,
          label: ch.title || `Chapter ${ch.chapter_number}`,
          readingTime: this.computeReadingTime(ch?.content),
        }));
        ordered.push({ act: nextAct++, title: k === "__no_act__" ? "Unassigned" : String(k), tiles });
      }
      return ordered;
    })();

    const titleText = selectedProject?.title
      ? `${selectedProject.title} live readings`
      : "Your live readings";

    const selectedChapter = this.getSelectedChapter();
    const overlayLines = this.getOverlayLines(selectedChapter?.content);
    // Pick latest video for the selected chapter (if any)
    const chapterVideo = (() => {
      if (!selectedChapter) return null;
      const list = Array.isArray(videos) ? videos : [];
      const items = list.filter(v => v && v.chapterId === selectedChapter.id);
      if (!items.length) return null;
      if (items[0]?.createdAt) {
        return items.slice().sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))[items.length - 1];
      }
      return items[items.length - 1];
    })();

    return (
      <div className="upload-widget">
        
        <section>

        <div className="header-row">
          <h3>{titleText}</h3>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }} />
        </div>

            {/* Project-type driven layout preview using chapters from context when available */}
            {selectedProject?.project_type_key ? (
              this.state.projectTypeLoading ? (
                <p>Loading layout…</p>
              ) : (
                <ActIconGrid
                  actCount={actCount}
                  tilesPerAct={6}
                  acts={(actsFromChapters && actsFromChapters.length) ? actsFromChapters : undefined}
                  videoChapterIds={videoChapterIds}
                  thumbByChapter={this.state.videoThumbByChapter}
                  onSelect={(tile) => {
                    // Open webcam for live reads and re-reads
                    if (tile?.action === 'live-read' || tile?.action === 're-read') {
                      this.openRecordModal(tile);
                    }
                  }}
                />
              )
            ) : (
              <FlatIconGrid
                columns={3}
                count={Math.max(1, activeChapters.length || 12)}
                tiles={activeChapters.map((ch, i) => ({
                  id: ch.id,
                  label: ch.title || `Chapter ${ch.chapter_number}`,
                  readingTime: this.computeReadingTime(ch?.content),
                }))}
                videoChapterIds={videoChapterIds}
                thumbByChapter={this.state.videoThumbByChapter}
                onSelect={(tile) => {
                  if (tile?.action === 'live-read' || tile?.action === 're-read') {
                    this.openRecordModal(tile);
                  }
                }}
              />
            )}

            {loading && <p>Loading videos…</p>}
            {error && <p className="error">{error}</p>}
            {!loading && !error && videos.length === 0 && <p>No videos found.</p>}

      
        </section>

        <LiveReadKaraoke

          isOpen={recordModalOpen}
          onClose={this.closeRecordModal}                              
          chapter={selectedChapter}
          projectId={selectedProject?.id}
          video={chapterVideo}
          overlayLines={overlayLines}
          readingTime={selectedChapter?.content ? this.computeReadingTime(selectedChapter.content) : null}
          onUploadComplete={(result) => {
            this.fetchVideos();
          }}
        />
      </div>
    );
  }
}

export default withRouter(withNotifications(LiveReadPage));
